import React from "react";
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Text,
  Textarea,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { useMutation } from "@apollo/client";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowForwardIcon, CloseIcon } from "@chakra-ui/icons";
import { logEvent } from "helpers/analytics";
import { gql } from "../../../__generated__";
import {
  ApiAccessCompanySize,
  ApiAccessIndustrySector,
  ApiAccessMonthlyCallVolume,
  ApiAccessPlatformStatus,
  ApiAccessUseCase,
} from "../../../__generated__/graphql";
import {
  apiAccessRequestSchema,
  type ApiAccessRequestFormData,
} from "../schema";
import { ThemedFormLabel } from "../../homeowners/hire/themed-form-label";
import { ThemedInput } from "../../homeowners/hire/themed-input";

const REQUEST_API_ACCESS = gql(`
  mutation requestApiAccess($apiAccessRequestDto: ApiAccessRequestDto!) {
    requestApiAccess(apiAccessRequestDto: $apiAccessRequestDto)
  }
`);

const industryOptions = [
  {
    value: ApiAccessIndustrySector.FacilityManagement,
    label: "Facility Management",
  },
  {
    value: ApiAccessIndustrySector.PropertyRealEstate,
    label: "Property / Real Estate",
  },
  {
    value: ApiAccessIndustrySector.Construction,
    label: "Construction",
  },
  {
    value: ApiAccessIndustrySector.Hospitality,
    label: "Hospitality",
  },
  {
    value: ApiAccessIndustrySector.CorporateOfficeManagement,
    label: "Corporate / Office Management",
  },
  {
    value: ApiAccessIndustrySector.TechnologySaas,
    label: "Technology / SaaS",
  },
  {
    value: ApiAccessIndustrySector.Other,
    label: "Other",
  },
] as const;

const companySizeOptions = [
  {
    value: ApiAccessCompanySize.OneToTenEmployees,
    label: "1 - 10 employees",
  },
  {
    value: ApiAccessCompanySize.ElevenToFifty,
    label: "11 - 50",
  },
  {
    value: ApiAccessCompanySize.FiftyOneToTwoHundred,
    label: "51 - 200",
  },
  {
    value: ApiAccessCompanySize.TwoHundredOneToFiveHundred,
    label: "201 - 500",
  },
  {
    value: ApiAccessCompanySize.FiveHundredPlus,
    label: "500+",
  },
] as const;

const useCaseOptions = [
  {
    value: ApiAccessUseCase.FacilityLocationManagement,
    label: "Facility & location management",
  },
  {
    value: ApiAccessUseCase.ServiceRequestSubmission,
    label: "Service request submission",
  },
  {
    value: ApiAccessUseCase.AssignmentVisitTracking,
    label: "Assignment & visit tracking",
  },
  {
    value: ApiAccessUseCase.QuotationsInvoicing,
    label: "Quotations & invoicing",
  },
  {
    value: ApiAccessUseCase.WebhookNotifications,
    label: "Webhook notifications",
  },
  {
    value: ApiAccessUseCase.Other,
    label: "Other",
  },
] as const;

const existingPlatformOptions = [
  {
    value: ApiAccessPlatformStatus.Yes,
    label: "Yes",
  },
  {
    value: ApiAccessPlatformStatus.BuildingSomethingNew,
    label: "No - building something new",
  },
  {
    value: ApiAccessPlatformStatus.ExploringOrNotSureYet,
    label: "Exploring / not sure yet",
  },
] as const;

const estimatedApiCallOptions = [
  {
    value: ApiAccessMonthlyCallVolume.Under_100,
    label: "Under 100",
  },
  {
    value: ApiAccessMonthlyCallVolume.Between_100And_1000,
    label: "100 - 1,000",
  },
  {
    value: ApiAccessMonthlyCallVolume.Between_1000And_10000,
    label: "1,000 - 10,000",
  },
  {
    value: ApiAccessMonthlyCallVolume.Over_10000,
    label: "10,000+",
  },
  {
    value: ApiAccessMonthlyCallVolume.NotSureYet,
    label: "Not sure yet",
  },
] as const;

interface ApiAccessRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const sectionHeadingStyles = {
  color: "gray.500",
  fontFamily: "Inter",
  fontSize: "sm",
  fontWeight: "semibold",
  letterSpacing: "0.22em",
  mb: 4,
  textTransform: "uppercase" as const,
};

const selectStyles = {
  borderColor: "gray.400",
  rounded: "none",
  size: "lg" as const,
};

const textareaStyles = {
  borderColor: "gray.400",
  minH: "140px",
  rounded: "none",
  size: "lg" as const,
};

export const ApiAccessRequestModal: React.FC<ApiAccessRequestModalProps> = ({
  isOpen,
  onClose,
}) => {
  const toast = useToast();
  const [requestApiAccess, { loading }] = useMutation(REQUEST_API_ACCESS);
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<ApiAccessRequestFormData>({
    resolver: zodResolver(apiAccessRequestSchema),
    defaultValues: {
      companyWebsite: "",
      estimatedMonthlyApiCalls: undefined,
      integrationDescription: "",
      phoneNumber: "",
      useCases: [],
    },
  });

  const selectedUseCases = watch("useCases");

  const handleModalClose = () => {
    reset();
    onClose();
  };

  const toggleUseCase = (value: ApiAccessUseCase) => {
    const nextUseCases = selectedUseCases.includes(value)
      ? selectedUseCases.filter((selectedUseCase) => selectedUseCase !== value)
      : [...selectedUseCases, value];

    setValue("useCases", nextUseCases, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  const onSubmit = handleSubmit(async (data) => {
    await requestApiAccess({
      variables: {
        apiAccessRequestDto: data,
      },
      onCompleted: () => {
        toast({
          title: "Request submitted",
          description:
            "We’ll review your API access request and get back to you within 2 business days.",
          duration: 5000,
          isClosable: true,
          status: "success",
        });
        logEvent("api_access_request_submitted");
        handleModalClose();
      },
      onError: (error) => {
        toast({
          title: "Submission failed",
          description:
            "Please try again or contact support if the issue persists.",
          duration: 5000,
          isClosable: true,
          status: "error",
        });
        logEvent("api_access_request_failed", { error: error.message });
      },
    });
  });

  if (!isOpen) return null;

  return (
    <Modal
      isCentered
      isOpen={isOpen}
      onClose={handleModalClose}
      closeOnOverlayClick={!loading}
      scrollBehavior="inside"
      size="2xl"
    >
      <ModalOverlay bg="rgba(18,18,18,0.5)" />
      <ModalContent rounded="none" mx={4}>
        <ModalHeader p={0}>
          <div className="flex min-h-20 items-center justify-end border-b border-prussianBlue-50 px-6">
            <IconButton
              aria-label="Close"
              icon={<CloseIcon />}
              isDisabled={loading}
              onClick={handleModalClose}
              rounded="full"
              size="sm"
              variant="outline"
              w={10}
              h={10}
            />
          </div>
          <div className="border-b border-prussianBlue-50 px-6 py-6">
            <h2 className="font-[AeonikPro] text-2xl font-bold leading-[1.2] text-prussianBlue-500 md:text-[2rem]">
              Request API access
            </h2>
            <Text mt={3} color="gray.600" fontFamily="Inter" fontSize="md" lineHeight="tall">
              Tell us about your business and how you plan to use the
              LaborHack API for facilities management and maintenance.
              We&apos;ll get back to you within 2 business days.
            </Text>
          </div>
        </ModalHeader>

        <ModalBody py={6} px={6}>
          <form onSubmit={onSubmit}>
            <VStack spacing={8} align="stretch">
              <Box>
                <Text {...sectionHeadingStyles}>Company information</Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl isInvalid={!!errors.companyName}>
                    <ThemedFormLabel htmlFor="companyName">
                      Company name *
                    </ThemedFormLabel>
                    <ThemedInput
                      id="companyName"
                      {...register("companyName")}
                      placeholder="e.g. Cresco Homes"
                    />
                    <FormErrorMessage>
                      {errors.companyName?.message}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errors.companyWebsite}>
                    <ThemedFormLabel htmlFor="companyWebsite">
                      Company website
                    </ThemedFormLabel>
                    <ThemedInput
                      id="companyWebsite"
                      {...register("companyWebsite")}
                      placeholder="https://..."
                    />
                    <FormErrorMessage>
                      {errors.companyWebsite?.message}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errors.industrySector}>
                    <ThemedFormLabel htmlFor="industrySector">
                      Industry / sector *
                    </ThemedFormLabel>
                    <Select
                      id="industrySector"
                      {...register("industrySector")}
                      {...selectStyles}
                      placeholder="Select"
                    >
                      {industryOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                    <FormErrorMessage>
                      {errors.industrySector?.message}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errors.companySize}>
                    <ThemedFormLabel htmlFor="companySize">
                      Company size *
                    </ThemedFormLabel>
                    <Select
                      id="companySize"
                      {...register("companySize")}
                      {...selectStyles}
                      placeholder="Select"
                    >
                      {companySizeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                    <FormErrorMessage>
                      {errors.companySize?.message}
                    </FormErrorMessage>
                  </FormControl>
                </SimpleGrid>
              </Box>

              <Box>
                <Text {...sectionHeadingStyles}>Contact details</Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl isInvalid={!!errors.fullName}>
                    <ThemedFormLabel htmlFor="fullName">
                      Full name *
                    </ThemedFormLabel>
                    <ThemedInput
                      id="fullName"
                      {...register("fullName")}
                      placeholder="First and last name"
                    />
                    <FormErrorMessage>
                      {errors.fullName?.message}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errors.jobTitleOrRole}>
                    <ThemedFormLabel htmlFor="jobTitleOrRole">
                      Job title / role *
                    </ThemedFormLabel>
                    <ThemedInput
                      id="jobTitleOrRole"
                      {...register("jobTitleOrRole")}
                      placeholder="e.g. CTO, Lead Engineer"
                    />
                    <FormErrorMessage>
                      {errors.jobTitleOrRole?.message}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errors.workEmail}>
                    <ThemedFormLabel htmlFor="workEmail">
                      Work email *
                    </ThemedFormLabel>
                    <ThemedInput
                      id="workEmail"
                      type="email"
                      {...register("workEmail")}
                      placeholder="you@company.com"
                    />
                    <FormErrorMessage>
                      {errors.workEmail?.message}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errors.phoneNumber}>
                    <ThemedFormLabel htmlFor="phoneNumber">
                      Phone number
                    </ThemedFormLabel>
                    <ThemedInput
                      id="phoneNumber"
                      type="tel"
                      {...register("phoneNumber")}
                      placeholder="+234..."
                    />
                    <FormErrorMessage>
                      {errors.phoneNumber?.message}
                    </FormErrorMessage>
                  </FormControl>
                </SimpleGrid>
              </Box>

              <Box>
                <Text {...sectionHeadingStyles}>Integration details</Text>

                <FormControl isInvalid={!!errors.useCases}>
                  <ThemedFormLabel>
                    What do you want to use the API for? *
                  </ThemedFormLabel>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                    {useCaseOptions.map((option) => {
                      const isSelected = selectedUseCases.includes(option.value);

                      return (
                        <Button
                          key={option.value}
                          type="button"
                          aria-pressed={isSelected}
                          borderColor={
                            isSelected ? "flatteredFlamingo.500" : "gray.300"
                          }
                          colorScheme={isSelected ? "flatteredFlamingo" : undefined}
                          fontFamily="Inter"
                          fontSize="md"
                          fontWeight="normal"
                          h="auto"
                          justifyContent="flex-start"
                          minH="64px"
                          onClick={() => toggleUseCase(option.value)}
                          px={4}
                          py={4}
                          rounded="none"
                          variant={isSelected ? "solid" : "outline"}
                          whiteSpace="normal"
                          _hover={{
                            borderColor: isSelected
                              ? "flatteredFlamingo.500"
                              : "gray.400",
                          }}
                        >
                          {option.label}
                        </Button>
                      );
                    })}
                  </SimpleGrid>
                  <FormErrorMessage>{errors.useCases?.message}</FormErrorMessage>
                </FormControl>

                <VStack spacing={4} align="stretch" mt={6}>
                  <FormControl isInvalid={!!errors.existingPlatformStatus}>
                    <ThemedFormLabel htmlFor="existingPlatformStatus">
                      Do you have an existing platform to integrate with? *
                    </ThemedFormLabel>
                    <Select
                      id="existingPlatformStatus"
                      {...register("existingPlatformStatus")}
                      {...selectStyles}
                      placeholder="Select"
                    >
                      {existingPlatformOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                    <FormErrorMessage>
                      {errors.existingPlatformStatus?.message}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errors.estimatedMonthlyApiCalls}>
                    <ThemedFormLabel htmlFor="estimatedMonthlyApiCalls">
                      Estimated monthly API calls
                    </ThemedFormLabel>
                    <Select
                      id="estimatedMonthlyApiCalls"
                      {...register("estimatedMonthlyApiCalls")}
                      {...selectStyles}
                      placeholder="Select"
                    >
                      {estimatedApiCallOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                    <FormErrorMessage>
                      {errors.estimatedMonthlyApiCalls?.message}
                    </FormErrorMessage>
                  </FormControl>
                </VStack>

                <FormControl isInvalid={!!errors.integrationDescription} mt={6}>
                  <ThemedFormLabel htmlFor="integrationDescription">
                    Brief description of your integration
                  </ThemedFormLabel>
                  <Textarea
                    id="integrationDescription"
                    {...register("integrationDescription")}
                    {...textareaStyles}
                    placeholder="Tell us about your facilities, how many locations you manage, and how LaborHack would fit into your workflow..."
                  />
                  <FormErrorMessage>
                    {errors.integrationDescription?.message}
                  </FormErrorMessage>
                </FormControl>
              </Box>

              <Button
                type="submit"
                isLoading={loading}
                loadingText="Submitting..."
                rightIcon={<ArrowForwardIcon />}
                size="lg"
                w="full"
                colorScheme="flatteredFlamingo"
              >
                Submit request
              </Button>
            </VStack>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
